import React from "react";
import {
  Clock3,
  CreditCard,
  LockKeyhole,
  CheckCircle2,
  RefreshCw,
} from "lucide-react";

import ContentPanel from "../../../../../components/layout/content/ContentPanel";
import Heading from "../../../../../components/typography/Heading";
import Text from "../../../../../components/typography/Text";
import Button from "../../../../../components/buttons/Button";

import "./VideoRentalPanel.css";

const formatDate = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export default function VideoRentalPanel({
  video,
  access,
  loading = false,
  renting = false,
  error = "",
  onRent,
  onRetry,
}) {
  const isFree = video?.isFree === true;

  const canWatch =
    isFree ||
    video?.canWatch === true ||
    access?.canWatch === true ||
    access?.hasAccess === true;

  const expiresAt = access?.expiresAt || access?.expires_at;

  const expired =
    access?.status === "expired" || access?.rentalStatus === "expired";

  const price =
    video?.displayPrice ||
    video?.rentalPriceFormatted ||
    (video?.rentalPrice != null
      ? `$${Number(video.rentalPrice).toFixed(2)} AUD`
      : null);

  const title = canWatch
    ? "Ready to watch"
    : expired
      ? "Your rental has expired"
      : "Unlock Video";

  return (
    <ContentPanel className="katch-rental-panel">
      <div className="katch-rental-panel__header">
        <div
          className={[
            "katch-rental-panel__icon",
            canWatch && "katch-rental-panel__icon--success",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {canWatch ? (
            <CheckCircle2 size={20} aria-hidden="true" />
          ) : (
            <LockKeyhole size={20} aria-hidden="true" />
          )}
        </div>

        <div className="katch-rental-panel__heading">
          <Heading as="h2" size="sm">
            {title}
          </Heading>

          <Text as="p" tone="secondary" size="sm">
            {canWatch
              ? "You have access to this video."
              : "Limited access only."}
          </Text>
        </div>
      </div>

      {expiresAt && canWatch && !isFree && (
        <div className="katch-rental-panel__expiry">
          <Clock3 size={16} aria-hidden="true" />

          <Text as="span" tone="secondary" size="sm">
            Available until {formatDate(expiresAt)}
          </Text>
        </div>
      )}

      {!canWatch && !loading && (
        <div className="katch-rental-panel__rental">
          {price && (
            <Heading as="h3" size="lg" className="katch-rental-panel__price">
              {price}
            </Heading>
          )}

          <div className="katch-rental-panel__duration">
            <Clock3 size={16} aria-hidden="true" />

            <Text as="span" tone="secondary" size="sm">
              5-day access from successful payment
            </Text>
          </div>

          <div className="katch-rental-panel__action">
            <Button
              value={
                renting
                  ? "Opening checkout..."
                  : expired
                    ? "Watch again"
                    : "Unlock video"
              }
              icon={<CreditCard size={17} />}
              variant="primary"
              onClick={onRent}
              disabled={renting || Boolean(error)}
            />
          </div>
        </div>
      )}

      {loading && (
        <Text
          as="p"
          tone="secondary"
          size="sm"
          className="katch-rental-panel__loading"
        >
          Checking your rental access...
        </Text>
      )}

      {error && (
        <div className="katch-rental-panel__error" role="alert">
          <Text as="p" size="sm">
            {typeof error === "string"
              ? error
              : error?.message || "Unable to verify rental access."}
          </Text>

          {onRetry && (
            <Button
              value="Check access again"
              icon={<RefreshCw size={16} />}
              variant="primary-outlined"
              onClick={onRetry}
              disabled={loading || renting}
            />
          )}
        </div>
      )}
    </ContentPanel>
  );
}
